import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { AlertCircle, CalendarDays, CheckCircle, Loader, RefreshCw, User, X, XCircle } from "lucide-react";
import { API_BASE_URL } from "../../../../../api";
import useRequestHistoryAutoRefresh from "./useRequestHistoryAutoRefresh";

const STATUS = {
    PENDING: { label: "En attente", bg: "#fef3c7", color: "#a16207" },
    ACCEPTED: { label: "Accepté", bg: "#dcfce7", color: "#15803d" },
    AUTO_ACCEPTED: { label: "Auto-accepté", bg: "#dcfce7", color: "#15803d" },
    REFUSED: { label: "Refusé", bg: "#fee2e2", color: "#b91c1c" },
    CANCELLED: { label: "Annulé", bg: "#e5e7eb", color: "#475569" },
};

const TABS = [
    { id: "pending", label: "À confirmer", match: (s) => s === "PENDING" },
    { id: "accepted", label: "Acceptés", match: (s) => s === "ACCEPTED" || s === "AUTO_ACCEPTED" },
    { id: "refused", label: "Refusés", match: (s) => s === "REFUSED" },
    { id: "cancelled", label: "Annulés", match: (s) => s === "CANCELLED" },
];

const day = (value) => String(value || "").slice(0, 10);

const authHeaders = (userId, json = false) => {
    const token = sessionStorage.getItem("token") || sessionStorage.getItem("accessToken");
    return {
        ...(json ? { "Content-Type": "application/json" } : {}),
        Authorization: token ? `Bearer ${token}` : "",
        uid: String(userId),
    };
};

/**
 * Onglet "Mes intérims" : demandes de remplacement reçues par le collaborateur.
 * Les dates affichées sont toujours celles du congé actuel (donc les nouvelles
 * dates en cas de report) ; les anciennes dates sont barrées si le backend les fournit.
 */
const MesInterims = ({ userId, formatDate }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("pending");
    const [refusing, setRefusing] = useState(null);
    const [refuseComment, setRefuseComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async (silent = false) => {
        if (!userId) return;
        if (!silent) setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/leave-requests/replacements/mine`, {
                headers: authHeaders(userId),
            });
            if (!response.ok) throw new Error("Impossible de charger les demandes de remplacement.");
            setItems(await response.json());
            setError("");
        } catch (err) {
            if (!silent) setError(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [userId]);

    useEffect(() => { load(); }, [load]);
    // Les reports, annulations et nouvelles désignations apparaissent sans recharger la page.
    useRequestHistoryAutoRefresh(() => load(true));

    const counts = useMemo(() => Object.fromEntries(
        TABS.map((tab) => [tab.id, items.filter((item) => tab.match(item.status)).length])
    ), [items]);

    const visible = useMemo(() => {
        const tab = TABS.find((entry) => entry.id === activeTab) || TABS[0];
        return items.filter((item) => tab.match(item.status));
    }, [items, activeTab]);

    const sendDecision = async (item, accepted, comment = "") => {
        setSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/leave-requests/replacements/${item.id}/decision`, {
                method: "POST",
                headers: authHeaders(userId, true),
                body: JSON.stringify({ accepted, comment }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.message || "Impossible d'enregistrer votre réponse.");
            await load(true);
            setRefusing(null);
            setRefuseComment("");
            Swal.fire({
                icon: "success",
                text: accepted ? "Disponibilité confirmée." : "Remplacement refusé.",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({ icon: "error", title: "Erreur", text: err.message });
        } finally {
            setSubmitting(false);
        }
    };

    const handleAccept = (item) => {
        Swal.fire({
            title: "Confirmation",
            text: "Confirmez-vous votre disponibilité pour assurer cet intérim ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#10b981",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, accepter",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (result.isConfirmed) sendDecision(item, true);
        });
    };

    const openRefuse = (item) => {
        setRefusing(item);
        setRefuseComment("");
    };

    const closeRefuse = () => {
        if (submitting) return;
        setRefusing(null);
        setRefuseComment("");
    };

    const renderPeriod = (item) => {
        const leaveStatus = String(item.leaveStatus || "").toUpperCase();
        const postponed = leaveStatus === "POSTPONED" || leaveStatus === "POSTPONEMENT_REQUEST";
        const changed = postponed && item.leaveOriginalStartDate
            && (day(item.leaveOriginalStartDate) !== day(item.leaveStartDate)
                || day(item.leaveOriginalEndDate) !== day(item.leaveEndDate));
        return (
            <>
                <div className="miPrimary">{formatDate(item.leaveStartDate)} → {formatDate(item.leaveEndDate)}</div>
                {changed && (
                    <div className="miOldDates">{formatDate(item.leaveOriginalStartDate)} → {formatDate(item.leaveOriginalEndDate)}</div>
                )}
                {postponed && (
                    <span className="miReport">
                        <RefreshCw size={11} />
                        {leaveStatus === "POSTPONEMENT_REQUEST" ? "Report en attente de validation" : "Congé reporté"}
                    </span>
                )}
            </>
        );
    };

    const renderStatus = (item) => {
        const config = STATUS[item.status] || STATUS.PENDING;
        const note = item.status === "REFUSED" ? item.responseComment
            : item.status === "CANCELLED" ? item.cancellationComment : null;
        return (
            <>
                <span className="miBadge" style={{ background: config.bg, color: config.color }}>{config.label}</span>
                {note && (
                    <div className="miNote" style={{ color: item.status === "REFUSED" ? "#b91c1c" : "#64748b" }}>
                        Motif : {note}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="miWrap">
            <h2 className="dcH2">Mes demandes de remplacement</h2>
            <p className="dcSub">Votre réponse confirme uniquement votre disponibilité et ne valide pas le congé. Sans action de votre part, l'intérim est accepté automatiquement le premier jour du congé.</p>

            {error && (
                <div className="dcAlert" style={{ marginTop: 12 }}>
                    <AlertCircle size={18} color="#9f1239" style={{ marginTop: 1 }} />
                    <div><h4>Erreur</h4><p>{error}</p></div>
                </div>
            )}

            <div className="miTabs">
                {TABS.map((tab) => (
                    <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`miTab ${activeTab === tab.id ? "is-active" : ""}`}>
                        {tab.label}
                        <span className={`miCount ${activeTab === tab.id ? "is-active" : ""}`}>{counts[tab.id]}</span>
                    </button>
                ))}
            </div>

            {loading && items.length === 0 ? (
                <div className="miEmpty"><Loader size={30} className="spin" /><div>Chargement…</div></div>
            ) : visible.length === 0 ? (
                <div className="miEmpty"><AlertCircle size={40} /><div>Aucune demande de remplacement dans cette catégorie.</div></div>
            ) : (
                <div className="dcTableWrap">
                    <table className="dcTable miTable">
                        <thead>
                        <tr>
                            <th>Collaborateur remplacé</th>
                            <th>Période du congé</th>
                            <th>Désigné par</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {visible.map((item) => (
                            <tr key={item.id}>
                                <td><span className="miPrimary">{item.employeeName}</span></td>
                                <td>{renderPeriod(item)}</td>
                                <td><span className="miMuted">{item.designatedByName || "—"}</span></td>
                                <td>{renderStatus(item)}</td>
                                <td>
                                    {item.status === "PENDING" ? (
                                        <div className="miActions">
                                            <button type="button" className="miActionBtn miActionBtn--ok" onClick={() => handleAccept(item)} disabled={submitting} title="Accepter l'intérim">
                                                <CheckCircle size={15} /> Accepter
                                            </button>
                                            <button type="button" className="miActionBtn miActionBtn--no" onClick={() => openRefuse(item)} disabled={submitting} title="Refuser l'intérim">
                                                <XCircle size={15} /> Refuser
                                            </button>
                                        </div>
                                    ) : <span className="miMuted">—</span>}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {refusing && (
                <div className="miModal" role="presentation" onMouseDown={closeRefuse}>
                    <div className="miModal__panel" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="miModal__header">
                            <div className="miModal__headLeft">
                                <div className="miModal__icon miModal__icon--danger"><AlertCircle size={18} /></div>
                                <div>
                                    <div className="miModal__title">Refuser l'intérim</div>
                                    <div className="miModal__sub">Intérim de {refusing.employeeName}</div>
                                </div>
                            </div>
                            <button type="button" className="miIconBtn" onClick={closeRefuse} aria-label="Fermer"><X size={18} /></button>
                        </div>

                        <div className="miModal__body">
                            <div className="miMiniCard">
                                <div className="miMiniRow">
                                    <span className="miMuted"><User size={13} /> Collaborateur</span>
                                    <b>{refusing.employeeName}</b>
                                </div>
                                <div className="miMiniRow">
                                    <span className="miMuted"><CalendarDays size={13} /> Période</span>
                                    <b>{formatDate(refusing.leaveStartDate)} → {formatDate(refusing.leaveEndDate)}</b>
                                </div>
                            </div>

                            <label className="miLabel">Motif du refus <span className="miReq">*</span></label>
                            <textarea
                                className="miTextarea"
                                rows={5}
                                value={refuseComment}
                                onChange={(e) => setRefuseComment(e.target.value)}
                                placeholder="Expliquez pourquoi vous n'êtes pas disponible..."
                                autoFocus
                            />
                            <div className="miHelp">Ce motif sera communiqué au collaborateur, au manager et aux RH.</div>
                        </div>

                        <div className="miModal__footer">
                            <button type="button" className="miBtn miBtn--ghost" onClick={closeRefuse} disabled={submitting}>Annuler</button>
                            <button
                                type="button"
                                className="miBtn miBtn--danger"
                                onClick={() => sendDecision(refusing, false, refuseComment.trim())}
                                disabled={!refuseComment.trim() || submitting}
                            >
                                {submitting ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesInterims;